import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import cm from 'classnames';
import { Drawer } from 'ksnm-common-ui/lib/Drawer';

import { Row } from '@common/Row';
import { usePermission } from '@src/utils/hooks/usePermission';
import { controlVersionsStore } from '@stores/dictionaryStores/controlVersionsStore/controlVersionsStore';
import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { period2PeriodStore } from '@stores/dictionaryStores/period2PeriodStore';
import { reportVersionsStore } from '@stores/dictionaryStores/reportVersion/reportVersionsStore';
import { logsStore } from '@stores/logsStore';

import styles from '../Logs.module.scss';

const ETL_TYPES_FOR_PERIOD_2_PERIOD = [29, 30, 31];

export const AdditionalOptionsDrawer = observer(() => {
  const { additionalOptions, setAdditionalOptions, etlTypes } = logsStore;
  const { reportVersions } = reportVersionsStore;
  const { controlVersions } = controlVersionsStore;
  const { period2period } = period2PeriodStore;

  const { VIEW: CmCtrlResultsView } = usePermission('CmCtrlResults');

  useEffect(() => {
    if (CmCtrlResultsView) fetchReferences(['controlVersions']);
    if (etlTypes.length > 0 && etlTypes.find((item) => item.id && ETL_TYPES_FOR_PERIOD_2_PERIOD.includes(item.id)))
      fetchReferences(['period2Period']);

    fetchReferences(['reportVersions']);
  }, [CmCtrlResultsView, etlTypes]);

  const versions = useMemo(
    () => (
      <>
        {additionalOptions?.reportVersions?.split(', ').map((version, idx) => {
          const versionName = reportVersions.find((item) => item.code === version)?.name;

          return <div key={idx}>{versionName ? `${version} - ${versionName}` : version}</div>;
        })}
      </>
    ),
    [additionalOptions?.reportVersions, reportVersions],
  );

  const controls = useMemo(
    () => (
      <>
        {additionalOptions?.controlVersions?.split(', ').map((version, idx) => {
          const controlVersion = controlVersions.find((item) => item.ctrlVersCode === version);

          return (
            <div key={idx}>
              {controlVersion
                ? `${controlVersion.ctrlCode} - ${controlVersion.ctrlVersExt} - ${controlVersion.ctrlVersDesc}`
                : version}
            </div>
          );
        })}
      </>
    ),
    [additionalOptions?.controlVersions, controlVersions],
  );

  const fns = useMemo(() => {
    const result: { [key: string]: string[] } | undefined = additionalOptions?.exportFNS
      ?.trim()
      .split('\n')
      .reduce((acc, line) => {
        const match = line.match(/Год (\d+)\. Период (\d+)\. Версия (\d+)\./);
        if (!match) return acc;

        const [_, year, period, version] = match;

        const periodName = period2period.find((el) => el.id === period)?.name;

        const key = `Год ${year}. Период ${periodName}.`;

        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(version);
        return acc;
      }, {});

    return result
      ? Object.entries(result)
          .map(([info, v]) => `${info} Версия ${v.join(', ')}.`)
          .join('\n')
      : '';
  }, [additionalOptions?.exportFNS, period2period]);

  return (
    <Drawer
      open={!!additionalOptions}
      onClose={() => setAdditionalOptions(null)}
      title="Дополнительные параметры"
      resizable
      zIndex={1015}
    >
      <Row name="GUID" value={additionalOptions?.guid} className={styles.row} />
      <Row name="Код пакета" value={additionalOptions?.reportPackageCode} className={styles.row} />
      <>
        {additionalOptions?.reportVersions && <Row name="Версии отчета" value={versions} className={styles.row} />}
        {additionalOptions?.controlVersions && <Row name="Версии контроля" value={controls} className={styles.row} />}
        {additionalOptions?.exportFNS && (
          <Row name="Период и версия данных" value={fns} className={cm(styles.row, styles.rowFns)} />
        )}
      </>
    </Drawer>
  );
});
